import React, { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import {
  Typography, FormControlLabel, Checkbox, Box, CircularProgress, TextField, InputAdornment, Chip,
  TableCell
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getStyles } from './styles';
import ColumnView from '../Common/ColumnView';
import SearchIcon from '@mui/icons-material/Search';
import DoneIcon from '@mui/icons-material/Done';
import { debounce } from 'lodash';
import useKey from '../../hooks/useKey';
import { getMatchingParts } from '../../util/filter';
import {
  checkChange,
  checkNodeChildren,
  checkSelectionsForEveryNode,
  isEveryChildChecked,
  isSomeChildCheckedOrIndeterminate
} from '../Common/ColumnView/service';

interface Props {
  nodes: BaseNode[];
  preSelectedNodeValues: string[];
  onSelectionsChange: (selectedNodes: string[]) => void;
  rosterWarning: string;
  isLoading: boolean;
  loadingProgress?: string;
  filterGrades?: string[];
  hideExpandAll?: boolean;
  getNodeChildren?: (node: any) => Promise<any>;
  getRootNode?: (node: any, appendChildren?: boolean) => Promise<any>;
  precheckedNodes?: BaseNode[];
}

type Branch = React.JSX.Element[];
type Page = Branch[];

const initialFilterTypes: { [key: string]: boolean } = {
  org: true,
  grade: true,
  course: true,
  class: true
}

const delay = (milliSeconds: number) => {
  return new Promise(
    (resolve) => setTimeout(
      () => resolve(true),
      milliSeconds
    )
  )
}

export default function ProvisionTreeSelections({ onSelectionsChange, getNodeChildren, getRootNode, loadingProgress, ...props }: Props) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const initialValue: string[] = [];
  const emptyNodesValue: NodeSet = [];
  const [checked, setChecked] = useState<Selections>(new Set());
  const [filterText, setFilterText] = useState('');
  const [nodes, setNodes] = useState(emptyNodesValue);
  const [filteredNodes, setFilteredNodes] = useState(emptyNodesValue);
  const [allSelected, setAllSelected] = useState([false, false]);
  const [filterTypes, setFilterTypes] = useState(initialFilterTypes);
  const [filterPages, setFilterPages] = useState<Page[]>([]);
  const [lastPage, setLastPage] = useState(0);
  const [selectAllByDepth, setSelectAllByDepth] = useState<[boolean, boolean][]>([]);
  const [selectedNode, setSelectedNode] = useState<string>();

  const filterFieldRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filterNodes = (nodes: NodeSet, isParentMatched?: boolean): [NodeSet, number] => {
    const replaceMatch = (index: number) => <Typography component="span" key={index} sx={{ color: theme.palette.primary.light, fontWeight: 'bold' }}>{filterText.toUpperCase()}</Typography>;
    const newNodes: NodeSet = [];
    let branchLeaves: number = 0;

    for (const node of nodes) {
      const matches = filterText && node.type && filterTypes[node.type.toLocaleLowerCase()] && getMatchingParts(node.label, filterText, replaceMatch);
      const newNode = { ...node, matches, leaves: 0 };
      const isCurrentNodeMatched = !!newNode.matches;

      if (newNode.children) {
        const [children, leaves] = filterNodes(newNode.children, isCurrentNodeMatched || isParentMatched);

        if (children && leaves) {
          branchLeaves += leaves;
          newNode.leaves = leaves;
          newNode.children = children;
          newNodes.push(newNode);
        }
      } else if (isCurrentNodeMatched || isParentMatched) {
        newNodes.push(newNode);
        branchLeaves++;
      }
    }
    return [newNodes, branchLeaves];
  };

  const assembleBranches = (nodes: NodeSet, callback?: (newBranch: BaseNode[]) => any, parents: BaseNode[] = []) => {
    for (const node of nodes) {
      const newNode = { ...node }
      if (node.children && node.children.length) {
        assembleBranches(node.children, callback, [...parents, newNode])
      } else {
        const newBranch = [];
        for (const parent of parents) {
          if (!parent.piledUp) {
            parent.piledUp = true;
            newBranch.push(parent)
          }
        }
        newBranch.push(newNode);
        if (callback) {
          callback(newBranch);
        }
      }
    }

  }

  const updateSelectAllFilterByDepth = (nodes: NodeSet, selectAll: [boolean, boolean][] = [], depth: number = 0) => {
    for (const node of nodes) {
      if (node.children) {
        updateSelectAllFilterByDepth(node.children, selectAll, depth + 1);
      }

      if (!selectAll[depth]) {
        selectAll[depth] = node.checked || [false, false];
      }

      const nodeStatus = node.checked || [false, false];
      selectAll[depth] = [selectAll[depth][0] || nodeStatus[0], selectAll[depth][1] && nodeStatus[1]];
    }
  }

  const updateSelectAllFilterNodes = (nodes: NodeSet) => {
    const newStatus: [boolean, boolean][] = [];
    updateSelectAllFilterByDepth(nodes, newStatus);
    setSelectAllByDepth(newStatus);
  }

  const checkFilterNodes = (node: BaseNode) => {
    const [_newRoot, newSelections] = checkNodeChildren(nodes, checked, node);
    onSelectionsChange([...newSelections]);
  }

  const memoCheckFilterNodes = useCallback(checkFilterNodes, [nodes, checked])

  const handleNavigateToSelectedNode = (event: React.MouseEvent<HTMLElement, MouseEvent>, node: BaseNode) => {
    if (event.ctrlKey) {
      event.preventDefault();
      setSelectedNode(node.value);
      handleClearFilterField();
    }
  }

  const filterBranches = async () => {
    if (filterText) {
      const newPages: Page[] = [];
      const filteredNodes: NodeSet = [];
      const chunkSize = 1;
      const minRenderedItems = 50;
      let renderedBranches = 0;

      for (let i = 0; i < nodes.length; i += chunkSize) {
        const slice = nodes.slice(i, i + chunkSize);
        const [newFilteredNodes] = filterNodes(slice);
        const newBranches: Branch[] = [];
        assembleBranches(newFilteredNodes, (newBranch) => {
          const newRow = newBranch.map(
            (node, index) => (
              <TableCell
                key={index}
                sx={{ verticalAlign: 'baseline', alignItems: 'baseline', border: node.leaves > 0 ? '1px solid #ccc' : 'none' }}
                rowSpan={node.leaves || 1}
              >
                <Box
                  onClick={event => {
                    if (event.ctrlKey) {
                      handleNavigateToSelectedNode(event, node);
                    } else {
                      memoCheckFilterNodes(node)
                    }
                  }}
                  onContextMenu={event => handleNavigateToSelectedNode(event, node)}
                  sx={[{
                    height: '100%'
                  }, styles.node]}>
                  <Typography component="span" sx={{ whiteSpace: 'nowrap', paddingRight: '1rem', }}>
                    <Checkbox
                      checked={node.checked && node.checked[0] && node.checked[1]}
                      indeterminate={node.checked && node.checked[0] !== node.checked[1]}
                      size='small'
                      color="primary"
                      sx={{ padding: '4px' }}
                    />
                    <span>{node.matches || node.label}</span>
                  </Typography>
                  {
                    node.leaves > 0 ?
                      <Box sx={styles.bracket}></Box>
                      : null
                  }
                </Box>
              </TableCell>
            )
          )
          newBranches.push(newRow);
        });

        if (newBranches.length > 0) {
          renderedBranches += newBranches.length;

          if (renderedBranches < minRenderedItems && lastPage < i) {
            setLastPage(Math.min(i, newPages.length));
          }

          newPages.push(newBranches);
        }

        setFilterPages([...newPages]);
        await delay(10);

        if (newFilteredNodes.length > 0) {
          filteredNodes.push(...newFilteredNodes);
          updateSelectAllFilterNodes(filteredNodes);
          setFilteredNodes([...filteredNodes]);
        }
      }
    }
  };

  const memoFilterBranches = useCallback(filterBranches, [nodes, checked, filterTypes, filterText]);

  const handleClearFilterField = () => {
    setFilterText('');
    resetBranchesContent();
    if (filterFieldRef.current) {
      filterFieldRef.current.value = "";
      filterFieldRef.current.focus();
    }
  }

  const getRootStatus = (nodes: NodeSet): [boolean, boolean] => {
    if (isEveryChildChecked(nodes)) {
      return [true, true];
    } else if (isSomeChildCheckedOrIndeterminate(nodes)) {
      return [true, false];
    } else {
      return [false, false];
    }
  }

  const updateSelectAll = (nodes: NodeSet) => {
    setAllSelected(getRootStatus(nodes));
  }

  useKey([
    {
      key: 'escape',
      handle: handleClearFilterField,
    },
  ]);

  useEffect(() => {
    memoFilterBranches();
  }, [memoFilterBranches])

  useEffect(() => {
    const nodesToDisplay = props.precheckedNodes ?? props.nodes;
    const selections = new Set(props.preSelectedNodeValues);
    const preCheckedNodes = checkSelectionsForEveryNode(nodesToDisplay, selections);
    setNodes(preCheckedNodes);
    setChecked(selections);
    updateSelectAll(preCheckedNodes);
  }, [props.nodes, props.preSelectedNodeValues, props.precheckedNodes]);

  const onCheck = ((checkedNodes: string[]) => {
    if (allSelected) {
      deselectAll();
    }
    updateSelectAll(nodes);
    onSelectionsChange(checkedNodes);
  });

  const resetBranchesContent = () => {
    setFilterPages([]);
    setLastPage(0);
  }

  const debounceOnChange = debounce((searchTerm) => {
    resetBranchesContent();
    setFilterText(searchTerm);
  }, 700);

  const getPages = () => {
    const content: Branch[] = [];
    for (let i = 0; i <= lastPage; i++) {
      if (filterPages[i]) {
        content.push(...filterPages[i]);
      }
    }
    return content.map((nodes, index) => <tr key={index}>{nodes}</tr>);
  }

  const selectAll = () => {
    const newSelected: string[] = [];

    if (!allSelected[1]) {
      nodes.forEach((node: any) => {
        newSelected.push(node.value);
      });
      setAllSelected([true, true]);
    } else {
      setAllSelected([false, false]);
    }

    onSelectionsChange(newSelected);
  }

  const deselectAll = () => {
    setAllSelected([false, false]);
    onSelectionsChange(initialValue);
  }

  const handleScroll = () => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight, } = listRef.current;
      const bottom = Math.ceil(scrollHeight - scrollTop) - 10 < clientHeight;

      if (bottom) {
        const maxPage = filterPages.length;
        const nextPage = (lastPage + 1) % maxPage;

        if (nextPage > 0) {
          setLastPage(nextPage);
        }
      }
    }
  }

  const memoHandleScroll = useCallback(handleScroll, [lastPage, filterPages]);

  const handleFilterTypeChange = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, type: string) => {
    if (event.shiftKey) {
      for (const key2 in filterTypes) {
        filterTypes[key2] = key2 === type ? true : false;
      }
    } else if (event.altKey) {
      for (const key2 in filterTypes) {
        filterTypes[key2] = key2 === type ? false : true;
      }
    } else {
      filterTypes[type] = !filterTypes[type];
    }
    setFilterTypes({ ...filterTypes })
  }

  const checkNodeByDepth = (root: NodeSet, selections: Selections, node: BaseNode, status: boolean, targetDepth = 0, currentDepth = 0): [NodeSet, Selections] => {
    let newRoot: NodeSet = root;
    let newSelections: Selections = selections;

    if (currentDepth === targetDepth) {
      [newRoot, newSelections] = checkChange(newRoot, newSelections, node, status);
    } else if (node.children && node.children.length) {
      for (const childNode of node.children) {
        const nextDepth = currentDepth + 1;
        [newRoot, newSelections] = checkNodeByDepth(newRoot, newSelections, childNode, status, targetDepth, nextDepth);
      }
    }
    return [newRoot, newSelections];
  }

  const checkAllFilteredNodes = (depth = 0) => {
    if (selectAllByDepth[depth] !== undefined) {
      let newRoot: NodeSet = nodes;
      let newSelections: Selections = checked;
      const newsStatus: [boolean, boolean] = [!selectAllByDepth[depth][0], !selectAllByDepth[depth][1]];
      const isChecked = newsStatus[0] || newsStatus[1];

      for (const filteredNode of filteredNodes) {
        [newRoot, newSelections] = checkNodeByDepth(newRoot, newSelections, filteredNode, isChecked, depth);
      }
      setNodes([...newRoot]);
      setChecked(new Set(newSelections));
      selectAllByDepth[depth] = newsStatus;
      setSelectAllByDepth([...selectAllByDepth]);
    }
  }

  const memoCheckAllFilteredNodes = useCallback(checkAllFilteredNodes, [nodes, checked, filteredNodes, selectAllByDepth])

  const filterField = (
    <Box
      component='span'
      sx={[{
        display: 'inline',
        m: 2
      }, styles.searchContainer]}>
      <TextField
        inputRef={filterFieldRef}
        label='Search'
        variant='outlined'
        id='searchTerm'
        sx={styles.search}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position='end'>
                <SearchIcon sx={styles.searchIcon} />
              </InputAdornment>
            ),
          }
        }}
        onChange={event => debounceOnChange(event.target.value)}
      />
      <Box>
        {
          Object.keys(filterTypes).map((type, index) => (
            <Chip
              key={index}
              label={type}
              color={filterTypes[type] ? "primary" : "default"}
              variant={filterTypes[type] ? "filled" : "outlined"}
              size="small"
              onClick={(event) => handleFilterTypeChange(event, type)}
              deleteIcon={<DoneIcon />}
            />
          ))
        }
      </Box>
    </Box>
  )

  const filterResult = (
    <Box sx={styles.searchResult} ref={listRef} onScroll={memoHandleScroll}>
      <table>
        <tbody>
          <tr>
            {
              selectAllByDepth.map((depth, index) => (
                <td key={index}>
                  <Typography component="span" sx={{ whiteSpace: 'nowrap' }} onClick={_event => memoCheckAllFilteredNodes(index)}>
                    <Checkbox
                      checked={depth[0] && depth[1]}
                      indeterminate={depth[0] !== depth[1]}
                      size='small'
                      color="primary"
                      sx={{ padding: '4px' }}
                    />
                    Select All
                  </Typography>
                </td>
              ))
            }
          </tr>
          {
            getPages()
          }
        </tbody>
      </table>
    </Box>
  )

  const loading = (
    <Box sx={styles.loaderContainer}>
      <CircularProgress />
      {loadingProgress}
    </Box>
  )

  const warning = (
    <Typography>{props.rosterWarning}</Typography>
  )

  const selections = (
    <Fragment>
      {
        nodes.length > 1 ?
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected[0] && allSelected[1]}
                indeterminate={allSelected[0] !== allSelected[1]}
                onChange={selectAll}
                name="checkedB"
                color="primary"
              />
            }
            label="Select All"
          /> :
          null
      }
      <ColumnView
        nodes={nodes}
        selections={checked}
        getNodeChildren={getNodeChildren}
        getRootNode={getRootNode}
        onCheck={onCheck}
        selectedNode={selectedNode}
      />
    </Fragment>
  )

  const noData = <Typography>No data to display...</Typography>

  return (
    <Typography component='div' className='filter-container'>
      <Box sx={styles.provisionTreeContainer}>
        {nodes.length > 0 ? filterField : null}
        <Typography
          component='div'
          sx={styles.checkboxTreeContainer}
        >
          {
            props.isLoading ? loading : (
              props.rosterWarning.length > 0 ? warning : (
                !nodes.length ? noData : (
                  filterText ? (filterPages.length ? filterResult : noData) : selections
                )
              )
            )
          }
        </Typography>
      </Box>
    </Typography>
  );
}
